import React from 'react';
import useLanguage from '../../hooks/useLanguage';
import { getPlatformDisplayName } from '../../utils/platformUtils';
import { money, percent, count } from './pricingFormat';

/**
 * Block 1 — what the unit achieves versus what it is asking.
 *
 * Four figures, equal weight, each carrying what it rests on. No trend arrows and no
 * sparklines: at ~12 units and a 90-day window there is not enough data to draw a
 * direction, and a trend line ops cannot substantiate is a claim we have not earned.
 */
/**
 * ⚠️ There is deliberately NO "difference" between the achieved and published rates.
 * The achieved figure is the trailing 90 days; the published one is the median of the
 * next 30 — a subtraction of the two looks authoritative while answering a question the
 * data cannot. On a unit repriced last month most of that difference IS the repricing.
 * A genuine comparison needs published-rate history we do not store (decision 25).
 */
const PricingScoreboard = ({ scoreboard, byChannel }) => {
  const { t } = useLanguage();
  // The only block on the page that reads a nested object. Guarded so a payload shape
  // change takes out one tile rather than the whole screen.
  const occupancy = scoreboard?.occupancy;
  if (!scoreboard || !occupancy) return null;

  const adr = money(scoreboard.achievedAdr);
  const typical = money(scoreboard.typicalPublishedPrice);

  // The gate on this release. `sampleNights` counts the occupied nights whose money is
  // actually keyed in; `occupiedNights` counts every occupied night. When they differ,
  // the rate and the occupancy figure on this same screen are describing different sets
  // of nights, and saying so is the difference between guidance and a confident guess.
  const sampleIsShort =
    typeof scoreboard.sampleNights === 'number' &&
    typeof scoreboard.occupiedNights === 'number' &&
    scoreboard.sampleNights < scoreboard.occupiedNights;

  return (
    <section className="pg-block">
      <div className="pg-scoreboard">
        {/* Achieved rate */}
        <div className="pg-stat">
          <div className="pg-stat__label">{t('pricing.scoreboard.achievedRate')}</div>
          {adr ? (
            <div className="pg-stat__value" data-figure>
              {adr}
              <span className="pg-stat__unit">{t('pricing.common.currency')}</span>
            </div>
          ) : (
            <div className="pg-stat__value pg-stat__value--none">
              {t('pricing.scoreboard.noData')}
            </div>
          )}
          <div className="pg-stat__meta">
            {t('pricing.scoreboard.achievedRateWindow')}
            {' · '}
            {t('pricing.common.exVat')}
            {adr && (
              <>
                {' · '}
                {t('pricing.scoreboard.basedOn')} {count(scoreboard.sampleNights)}{' '}
                {t('pricing.common.nights')}
              </>
            )}
          </div>
        </div>

        {/* Forward occupancy — or an honest statement that there is nothing to sell */}
        <div className={`pg-stat${occupancy.nothingSellable ? ' pg-stat--held' : ''}`}>
          <div className="pg-stat__label">{t('pricing.scoreboard.occupancy')}</div>
          {occupancy.nothingSellable ? (
            <div className="pg-stat__value" data-figure>
              {t('pricing.scoreboard.nothingSellable')}
            </div>
          ) : (
            <div className="pg-stat__value" data-figure>
              {percent(occupancy.rate)}
            </div>
          )}
          <div className="pg-stat__meta">
            {occupancy.nothingSellable ? (
              t('pricing.scoreboard.nothingSellableHint')
            ) : (
              <>
                {t('pricing.scoreboard.occupancyWindow')}
                {' · '}
                {count(occupancy.bookedNights)} {t('pricing.scoreboard.occupancySold')}{' '}
                {t('pricing.common.of')} {count(occupancy.sellableNights)}{' '}
                {t('pricing.scoreboard.occupancySellable')}
                {/* Nights held for a relocated guest are physically unavailable, so they
                    never entered the sellable base. Stated, not silently netted off. */}
                {occupancy.heldNights > 0 && (
                  <>
                    {' · '}
                    {count(occupancy.heldNights)} {t('pricing.scoreboard.heldNights')}
                  </>
                )}
              </>
            )}
          </div>
        </div>

        {/* Typical published price */}
        <div className="pg-stat">
          <div className="pg-stat__label">{t('pricing.scoreboard.typicalPrice')}</div>
          {typical ? (
            <div className="pg-stat__value" data-figure>
              {typical}
              <span className="pg-stat__unit">{t('pricing.common.currency')}</span>
            </div>
          ) : (
            <div className="pg-stat__value pg-stat__value--none">
              {t('pricing.scoreboard.noData')}
            </div>
          )}
          <div className="pg-stat__meta">
            {t('pricing.scoreboard.typicalPriceWindow')}
            {' · '}
            {t('pricing.common.exVat')}
          </div>
        </div>

        {/* Unsold nights */}
        <div className="pg-stat">
          <div className="pg-stat__label">{t('pricing.scoreboard.unsold')}</div>
          <div className="pg-stat__value" data-figure>
            {count(scoreboard.unsoldNights)}
          </div>
          <div className="pg-stat__meta">{t('pricing.scoreboard.unsoldWindow')}</div>
        </div>
      </div>

      {/* Secondary by design: the main read stays the two rates. A blended achieved rate
          says "we are underperforming"; the split says which channel to look at. */}
      {Array.isArray(byChannel) && byChannel.length > 1 && (
        <div className="pg-channels" title={t('pricing.scoreboard.byChannelHint')}>
          <span className="pg-channels__label">{t('pricing.scoreboard.byChannel')}:</span>
          {byChannel.map(c => (
            <span className="pg-channels__item" key={c.platform} data-figure>
              {getPlatformDisplayName(c.platform)} {money(c.achievedRate)}{' '}
              {t('pricing.common.currency')}
            </span>
          ))}
        </div>
      )}

      {sampleIsShort && (
        <p className="pg-caveat">
          {t('pricing.scoreboard.caveatPrefix')} {count(scoreboard.sampleNights)}{' '}
          {t('pricing.scoreboard.caveatMiddle')} {count(scoreboard.occupiedNights)}{' '}
          {t('pricing.scoreboard.caveatSuffix')}
        </p>
      )}
    </section>
  );
};

export default PricingScoreboard;
